import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check2Square } from 'react-bootstrap-icons';
import { removeBetFromBetSlip, removeBetBuilderGroup, setBetSlipBanker, setBetSlipEnabled } from 'stores/betSlip';
import { getMatchTime, MarketIdToMarketName, OddsValueTypeTextCreator } from 'toolkit/utils';
import { getMatchDetail, getTeamNameHelper } from 'helper/matchDetailHelper';
import { sportTypeToSportId } from 'toolkit/utils.js';
import { AWS_BUCKET_URL } from 'toolkit/constants';
import { EnumMarketName, EnumEventStatus } from 'toolkit/Enums.js';
import { updateBetSlipBetsOdds } from 'stores/betSlip';
import { gameDateToMatchDay, gameDateToMatchTime } from 'toolkit/utils';
import useOddTransformer from 'toolkit/useHooks/useOddsTransformer';
import { checkIfLiveEvent } from 'toolkit/utils';
import { marketDisplayName } from 'components/outrights/utils';
import { selectEventById as selectOutrightEventById } from 'stores/outrights';

export const BetSlipRow = ({ bet }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('translations', { useSuspense: false });
  const betSlip = useSelector((state) => state.betSlip);
  const match = useSelector((state) => state.match.events[bet.providerId]);
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const [oddsClass, setOddsClass] = useState('');
  const oddsTransformer = useOddTransformer();
  const prevOddsRef = useRef(null);
  let marketName = '';
  let playerOrTeam = '';

  // Outright bets aren't stored in state.match.events. Prefer live data from
  // the outrights store (so odds updates flow through), then fall back to the
  // bet's embedded snapshot built by OutrightOddButton.buildOutrightMatch.
  const isOutright = !!bet?.match?.isOutright;
  const liveOutrightEvent = useSelector((state) =>
    isOutright ? selectOutrightEventById(state, bet.providerId) : null
  );
  const sourceMatch = isOutright
    ? (liveOutrightEvent
        ? { ...bet.match, markets: liveOutrightEvent.markets || bet.match.markets }
        : bet.match)
    : match;

  const { matchInfo, outcomeData } = useMemo(() => {
    if (!sourceMatch) {
      return { matchInfo: null, outcomeData: { odds: bet.odds || 0 } };
    }

    let info;
    if (isOutright) {
      info = {
        home: { id: sourceMatch.providerId, name: sourceMatch.eventName, homeScore: 0 },
        away: { id: '', name: '', awayScore: 0 }
      };
    } else {
      info = getMatchDetail(sourceMatch);
    }

    const marketEntry = sourceMatch?.markets?.find((m) => m.stringId === bet.marketStringId);
    const outcome = marketEntry?.outcomes?.find((o) => o.id === bet.outcomeId);

    if (!outcome) {
      if (isOutright) {
        return {
          matchInfo: info,
          outcomeData: { id: bet.outcomeId, name: bet.outcomeName, odds: bet.odds || 0 }
        };
      }
      dispatch(
        removeBetFromBetSlip({
          providerId: bet.providerId,
          marketStringId: bet.marketStringId,
          outcomeId: bet.outcomeId
        })
      );
      return { matchInfo: info, outcomeData: { odds: 0 } };
    }

    return { matchInfo: info, outcomeData: outcome };
  }, [sourceMatch, isOutright, bet.marketStringId, bet.outcomeId, bet.outcomeName, bet.odds, bet.providerId, dispatch]);

  useEffect(() => {
    const newOdd = outcomeData?.odds;
    if (!prevOddsRef.current) {
      prevOddsRef.current = newOdd;
      return;
    }
    const prevOdd = prevOddsRef.current;

    if (newOdd !== prevOdd) {
      setOddsClass(`odd-${newOdd > prevOdd ? 'increased-slip' : 'decreased-slip'}`);
      prevOddsRef.current = newOdd;

      const highlightResetTimer = setTimeout(() => {
        setOddsClass('');
      }, 5000);

      return () => {
        clearTimeout(highlightResetTimer);
      };
    }
  }, [outcomeData?.odds]);

  const removeOdds = useCallback((b) => {
    // Check if it's a bet builder bet
    if (b.isBetBuilder && b.betBuilderId !== undefined) {
      // Always delete the entire bet builder group (all custom bets)
      dispatch(removeBetBuilderGroup({ betBuilderId: b.betBuilderId }));
    } else {
      // Normal bet deletion
      dispatch(
        removeBetFromBetSlip({
          providerId: b.providerId,
          marketStringId: b.marketStringId,
          outcomeId: b.outcomeId
        })
      );
    }
  }, [dispatch]);

  const toggleMatch = useCallback(() => {
    dispatch(setBetSlipEnabled({ providerId: bet.providerId, enabled: !bet.enabled }));
  });

  const toggleBanker = useCallback(() => {
    dispatch(setBetSlipBanker({ providerId: bet.providerId, banker: !bet.banker }));
  });

  const navigateToMatchDetail = useCallback(() => {
    if (!bet?.providerId) return;
    navigate(`/match-detail#${bet.providerId}`, {
      state: { scrollToMarketHeader: true }
    });
  }, [bet?.providerId, navigate]);

  {
    if (!sourceMatch || !matchInfo) return <div />;
  }

  return (
    <div
      className="bet-slip-row fs-8"
      style={{ opacity: bet.enabled ? 1 : 0.3, cursor: 'pointer' }}
      onClick={navigateToMatchDetail}>
      <Row className="px-3 text-color-white">
        <div className="col-auto">
          <div
            onClick={(event) => {
              event.stopPropagation();
              toggleMatch();
            }}>
            <Check2Square fill={bet.enabled ? 'green' : 'grey'} size={22}></Check2Square>
          </div>
        </div>
        <div className="col">
          <div className="d-flex justify-content-between mb-2">
            <span>
              {isOutright
                ? sourceMatch.eventName
                : `${getTeamNameHelper(locales, selectedLocale, matchInfo.home, t)} - ${getTeamNameHelper(locales, selectedLocale, matchInfo.away, t)}`}
            </span>
            <div className="col-auto">
              {betSlip.system ? (
                <span>
                  <div
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleBanker();
                    }}>
                    <div
                      className="banker-bg"
                      style={{ backgroundColor: bet.banker ? '#3AA843' : '#E5E5E5' }}>
                      <span className="text-white fw-bold fs-8 m-0 d-flex justify-content-center">
                        B
                      </span>
                    </div>
                  </div>
                </span>
              ) : null}
            </div>
            {isOutright ? null : sourceMatch.eventStatus == EnumEventStatus.RUNNING ? (
              <span>
                {matchInfo.home.homeScore} : {matchInfo.away.awayScore}
              </span>
            ) : (
              <span>
                <div>{gameDateToMatchDay(sourceMatch.gameDate)}</div>
                <div>{gameDateToMatchTime(sourceMatch.gameDate)}</div>
              </span>
            )}
          </div>
          <hr className="mt-1 mb-1" />
          {betSlip.bets.map((b) => {
            // Must match providerId (same match)
            if (b.providerId !== bet.providerId) {
              return null;
            }
            
            // For bet builder bets: only show bets in the same bet builder group (same betBuilderId)
            if (bet.isBetBuilder) {
              if (!b.isBetBuilder || b.betBuilderId !== bet.betBuilderId) {
                return null;
              }
            } else {
              // For normal bets: only show normal bets (exclude bet builder bets)
              if (b.isBetBuilder) {
                return null;
              }
            }
            
            const bIsOutright = !!b.match?.isOutright;
            const bSourceMatch = bIsOutright
              ? (liveOutrightEvent
                  ? { ...b.match, markets: liveOutrightEvent.markets || b.match.markets }
                  : b.match)
              : match;
            const sportForOdds = bSourceMatch?.sport != null ? bSourceMatch.sport.toString() : '0';
            const market = bSourceMatch?.markets?.find((m) => m.stringId === b.marketStringId);
            let outcome = market?.outcomes?.find((o) => o.id === b.outcomeId);
            if (outcome === undefined || outcome === null)
              outcome = { id: b.outcomeId, name: b.outcomeName, odds: 0 };
            let playerName;
            let name;
            let surname;
            const outcomeName = outcome.name || '';
            if (outcomeName.includes('&')) {
              playerName = outcomeName.split('&')[0];
              name = playerName.split(',')[1] ?? "";
              surname = playerName.split(',')[0] ?? "";
            }
            else {
              name = outcomeName.split(',')[1] ?? "";
              surname = outcomeName.split(',')[0] ?? "";
            }
            playerName = name + ' ' + surname;

            if (bIsOutright) {
              const outrightMarket = market || { stringId: b.marketStringId, name: '' };
              marketName = marketDisplayName(outrightMarket, t);
              const rawOutcome = b.outcomeName || outcome.name || '';
              if (rawOutcome.includes(',')) {
                const parts = rawOutcome.split(',');
                playerOrTeam = ((parts[1] || '').trim() + ' ' + (parts[0] || '').trim()).trim();
              } else {
                playerOrTeam = rawOutcome;
              }
            } else if (b.marketId == 534) {
              if (b.outcomeName?.includes(',')) {
                marketName = t('whogoalscorer');
                playerOrTeam = b.outcomeName.split(',')[1] + ' ' + b.outcomeName.split(',')[0];
              } else {
                marketName = t('whochampion');
                playerOrTeam = OddsValueTypeTextCreator(
                  sportForOdds,
                  b.marketId.toString(),
                  outcome.id,
                  outcome.name
                );
              }
            } else {
              marketName = MarketIdToMarketName(b.marketId, b.marketStringId, false);
            }
            return (
                <div key={b.providerId + b.marketStringId + outcome.id} className="py-3 px-0">
                  <div className="d-flex justify-content-between">
                    <div className="col-4">
                      <span>{marketName}</span>
                    </div>
                    <div className='col-1'></div>
                    <div className="col-3">

                      <span>
                        {b.marketId === EnumMarketName.ANYTIMEGOALSCORERWINNER_888 ||
                          b.marketId === EnumMarketName.NEXTGOALSCORERWINNER_891
                          ?
                          playerName +
                          ',' + OddsValueTypeTextCreator(
                            sportForOdds,
                            b.marketId.toString(),
                            outcome.id,
                            outcome.name
                          )
                          : b.marketId === EnumMarketName.ANYTIMEGOALSCORER_40 ||
                            b.marketId === EnumMarketName.NEXTGOALSCORER_38 ||
                            b.marketId === EnumMarketName.LASTGOALSCORER_39
                            ? OddsValueTypeTextCreator(
                              sportForOdds,
                              b.marketId.toString(),
                              outcome.id,
                            ) +
                            ',' +
                            playerName
                            : (bIsOutright || b.marketId == 534)
                            ? playerOrTeam
                            : OddsValueTypeTextCreator(
                                sportForOdds,
                                b.marketId.toString(),
                                outcome.id,
                                outcome.name
                              )}
                      </span>
                    </div>
                    <div className='col-1'></div>
                    <div className="col-2">
                      {/* Hide odds for custom bet rows - only show for normal bets */}
                      {!b.isBetBuilder && market && (
                        <>
                          {market.marketStatus !== 6 && (
                            <img
                              width="20"
                              height="20"
                              className="light-black-svg"
                              src={`${AWS_BUCKET_URL}/Icons/lockicon.svg`}
                            />
                          )}
                          {market.marketStatus === 6 && (
                            <span className={oddsClass}>
                              {oddsTransformer.transformWithDisplayFormatted(
                                checkIfLiveEvent(b?.match),
                                b?.match?.isSeason,
                                b?.marketId,
                                new Date(b?.match?.gameDate),
                                b?.odds,
                                b?.marketStringId
                              )}
                            </span>
                          )}
                          <br />
                          {!bIsOutright && bSourceMatch?.eventStatus == EnumEventStatus.RUNNING &&
                            bSourceMatch?.sport === sportTypeToSportId('Football') &&
                            getMatchTime(bSourceMatch, t)}
                          {!bIsOutright && bSourceMatch?.eventStatus == EnumEventStatus.RUNNING &&
                            bSourceMatch?.sport !== sportTypeToSportId('Football') && (
                              <span>
                                {t(
                                  `components.matchcard.time.byMatchStatusId.${bSourceMatch?.matchStatusId}`
                                )}
                              </span>
                            )}
                        </>
                      )}
                    </div>
                    <div className="col-1">
                      <span>
                        <div
                          onClick={(event) => {
                            event.stopPropagation();
                            removeOdds(b);
                          }}>
                          <img src={`${AWS_BUCKET_URL}/Icons/116.png`}></img>
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
              );
          })}
        </div>
      </Row>
    </div>
  );
};
